/**
 * TAG SELECTOR COMPONENT
 * 
 * An accordion-style component for selecting structured tags.
 * Supports both single-select (radio) and multi-select (checkbox) categories.
 * 
 * Features:
 * - Expandable/collapsible sections
 * - Shows selected count when collapsed
 * - Displays selected items as colored chips
 * - Uses TAG_CATEGORIES metadata for dynamic rendering
 */

import React, { useState } from 'react';
import { ChevronDown, ChevronRight, X } from 'lucide-react';
import { TAG_CATEGORIES } from '../types/index';
import type {
  BaseType,
  Finish,
  HoloType,
  ColorShift,
  Particle,
  SpecialFeature,
  ColorFamily,
  Utility,
  ColorCharacter
} from '../types/index';

// ============================================================================
// TYPES
// ============================================================================

interface TagSelectorProps {
  // Current values for each category
  baseType?: BaseType;
  finish?: Finish[];
  holoType?: HoloType[];
  colorShift?: ColorShift;
  particles?: Particle[];
  specialFeatures?: SpecialFeature[];
  colorFamily?: ColorFamily[];
  utility?: Utility[];
  colorCharacter?: ColorCharacter[];
  
  // Callback when any tag changes
  onChange: (category: string, value: any) => void;
  
  // Optional: which sections start expanded
  defaultExpanded?: string[];
  
  // Optional: read-only mode (just display, no editing)
  readOnly?: boolean;
}

// Color mapping for chips by category
const CATEGORY_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  baseType: { bg: 'bg-amber-100', text: 'text-amber-800', border: 'border-amber-300' },
  finish: { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-300' },
  holoType: { bg: 'bg-violet-100', text: 'text-violet-800', border: 'border-violet-300' },
  colorShift: { bg: 'bg-fuchsia-100', text: 'text-fuchsia-800', border: 'border-fuchsia-300' },
  particles: { bg: 'bg-pink-100', text: 'text-pink-800', border: 'border-pink-300' },
  specialFeatures: { bg: 'bg-orange-100', text: 'text-orange-800', border: 'border-orange-300' },
  colorFamily: { bg: 'bg-emerald-100', text: 'text-emerald-800', border: 'border-emerald-300' },
  utility: { bg: 'bg-slate-100', text: 'text-slate-800', border: 'border-slate-300' },
  colorCharacter: { bg: 'bg-purple-100', text: 'text-purple-800', border: 'border-purple-300' }
};

// ============================================================================
// COMPONENT
// ============================================================================

const TagSelector: React.FC<TagSelectorProps> = ({
  baseType,
  finish = [],
  holoType = [],
  colorShift,
  particles = [],
  specialFeatures = [],
  colorFamily = [],
  utility = [],
  colorCharacter = [],
  onChange,
  defaultExpanded = [],
  readOnly = false
}) => {
  // Track which sections are expanded
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(defaultExpanded)
  );

  // Get current value for a category
  const getValue = (key: string): any => {
    switch (key) {
      case 'baseType': return baseType;
      case 'finish': return finish;
      case 'holoType': return holoType;
      case 'colorShift': return colorShift;
      case 'particles': return particles;
      case 'specialFeatures': return specialFeatures;
      case 'colorFamily': return colorFamily;
      case 'utility': return utility;
      case 'colorCharacter': return colorCharacter;
      default: return undefined;
    }
  };

  // Toggle section expansion
  const toggleSection = (key: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  // Handle single-select change
  const handleSingleSelect = (categoryKey: string, value: string) => {
    if (readOnly) return;
    // If clicking the already-selected value, deselect it
    const currentValue = getValue(categoryKey);
    if (currentValue === value) {
      onChange(categoryKey, undefined);
    } else {
      onChange(categoryKey, value);
    }
  };

  // Handle multi-select change
  const handleMultiSelect = (categoryKey: string, value: string) => {
    if (readOnly) return;
    const currentValues = getValue(categoryKey) || [];
    if (currentValues.includes(value)) {
      // Remove it
      onChange(categoryKey, currentValues.filter((v: string) => v !== value));
    } else {
      // Add it
      onChange(categoryKey, [...currentValues, value]);
    }
  };

  // Remove a chip (for multi-select)
  const removeChip = (categoryKey: string, value: string) => {
    if (readOnly) return;
    const currentValues = getValue(categoryKey) || [];
    onChange(categoryKey, currentValues.filter((v: string) => v !== value));
  };

  // Get selected count for display
  const getSelectedCount = (key: string, multiSelect: boolean): number => {
    const value = getValue(key);
    if (multiSelect) {
      return Array.isArray(value) ? value.length : 0;
    } else {
      return value ? 1 : 0;
    }
  };

  // Render chips for selected values
  const renderSelectedChips = (categoryKey: string, multiSelect: boolean) => {
    const value = getValue(categoryKey);
    const colors = CATEGORY_COLORS[categoryKey] || CATEGORY_COLORS.baseType;
    
    if (multiSelect) {
      if (!Array.isArray(value) || value.length === 0) return null;
      return (
        <div className="flex flex-wrap gap-1 mt-2">
          {value.map((v: string) => (
            <span
              key={v}
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${colors.bg} ${colors.text} border ${colors.border}`}
            >
              {v}
              {!readOnly && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeChip(categoryKey, v);
                  }}
                  className="hover:bg-black/10 rounded-full p-0.5"
                >
                  <X className="icon-xs" />
                </button>
              )}
            </span>
          ))}
        </div>
      );
    } else {
      if (!value) return null;
      return (
        <div className="flex flex-wrap gap-1 mt-2">
          <span
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${colors.bg} ${colors.text} border ${colors.border}`}
          >
            {value}
            {!readOnly && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onChange(categoryKey, undefined);
                }}
                className="hover:bg-black/10 rounded-full p-0.5"
              >
                <X className="icon-xs" />
              </button>
            )}
          </span>
        </div>
      );
    }
  };

  return (
    <div className="space-y-2">
      {TAG_CATEGORIES.map((category) => {
        const isExpanded = expandedSections.has(category.key);
        const selectedCount = getSelectedCount(category.key, category.multiSelect);
        const colors = CATEGORY_COLORS[category.key] || CATEGORY_COLORS.baseType;
        
        return (
          <div
            key={category.key}
            className="border border-gray-200/60 bg-white/40 backdrop-blur-sm rounded-lg overflow-hidden shadow-sm"
          >
            {/* Header - Always visible */}
            <button
              type="button"
              onClick={() => toggleSection(category.key)}
              aria-expanded={isExpanded}
              aria-controls={`${category.key}-content`}
              className="w-full flex items-center justify-between px-4 py-3 bg-white/60 backdrop-blur-sm hover:bg-white/80 transition-all"
            >
              <div className="flex items-center gap-3">
                <span className="text-xl">
                  {typeof category.icon === 'string' 
                    ? category.icon 
                    : category.icon && React.createElement(category.icon, { className: 'w-5 h-5' })
                  }
                </span>
                <span className="font-medium text-gray-900">{category.label}</span>
                {selectedCount > 0 && (
                  <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${colors.bg} ${colors.text}`}>
                    {selectedCount}
                  </span>
                )}
              </div>
              {isExpanded ? (
                <ChevronDown className="icon-md text-gray-500" />
              ) : (
                <ChevronRight className="icon-md text-gray-500" />
              )}
            </button>

            {/* Collapsed view - Show chips */}
            {!isExpanded && selectedCount > 0 && (
              <div className="px-4 pb-3 bg-white/60 backdrop-blur-sm">
                {renderSelectedChips(category.key, category.multiSelect)}
              </div>
            )}

            {/* Expanded view - Show all options */}
            {isExpanded && (
              <div id={`${category.key}-content`} className="px-4 py-3 bg-white/80 backdrop-blur-sm">
                <p className="text-xs text-gray-500 mb-3">{category.description}</p>
                
                {/* Options grid */}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {category.options.map((option) => {
                    const isSelected = category.multiSelect
                      ? (getValue(category.key) || []).includes(option)
                      : getValue(category.key) === option;
                    
                    return (
                      <label
                        key={option}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-all ${
                          isSelected
                            ? `${colors.bg} ${colors.text} border-2 ${colors.border} shadow-sm`
                            : 'bg-white/60 backdrop-blur-sm text-gray-600 border-2 border-gray-200/60 hover:bg-white/90 hover:border-gray-300'
                        } ${readOnly ? 'cursor-default' : ''}`}
                      >
                        <input
                          type={category.multiSelect ? 'checkbox' : 'radio'}
                          name={category.key}
                          value={option}
                          checked={isSelected}
                          onChange={() => {
                            if (category.multiSelect) {
                              handleMultiSelect(category.key, option);
                            } else {
                              handleSingleSelect(category.key, option);
                            }
                          }}
                          disabled={readOnly}
                          className="sr-only" // Hide the actual input, style the label
                        />
                        <span className="text-sm font-medium capitalize">{option}</span>
                        {isSelected && (
                          <span className="ml-auto text-sm">✓</span>
                        )}
                      </label>
                    );
                  })}
                </div>

                {/* Selected chips in expanded view */}
                {renderSelectedChips(category.key, category.multiSelect)}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default TagSelector;
